import { z } from "zod"

import {
  stringNoNumbersOrSpecialCharacters,
  numberLikeGreaterThanZero,
  dateString,
  numberLike,
  last4ssn,
} from "./form-fields"
import {
  stateCodePattern,
  zipCodePattern,
  mosaicPhoneNumberFormat,
} from "./regex"

export const mosaicPaymentSchema = z.object({
  monthlyPayment: z.number().min(0),
  months: z.number().min(1),
  monthlyPaymentWithoutAchDiscount: z.number().min(0),
})

export type MosaicPayment = z.infer<typeof mosaicPaymentSchema>

export const mosaicPaymentsSchema = z.array(mosaicPaymentSchema)

export const secondaryApplicantSchema = z.object({
  secondary_firstName: stringNoNumbersOrSpecialCharacters.refine(
    (v) => v.length > 1,
    `Must be at least 2 characters long`,
  ),
  secondary_middleName: stringNoNumbersOrSpecialCharacters.optional(),
  secondary_lastName: stringNoNumbersOrSpecialCharacters.refine(
    (v) => v.length > 1,
    `Must be at least 2 characters long`,
  ),
  secondary_dob: z
    .string({ description: `date of birth is required` })
    .refine((dob) => !isNaN(Date.parse(dob)), `must be a valid date`),
  secondary_ssn: last4ssn,
  secondary_street: z.string().min(1, `required`),
  secondary_city: z.string().min(1, `required`),
  secondary_state: z.string().regex(stateCodePattern),
  secondary_zip: z.string().regex(zipCodePattern),
  secondary_phoneNumber: z.string().regex(mosaicPhoneNumberFormat),
  secondary_email: z.string().email(),
  secondary_annualIncome: numberLikeGreaterThanZero,
  secondary_ownership: z.boolean(),
  secondary_resident: z.boolean(),
  secondary_agreeTerms: z
    .boolean()
    .refine((agreeTerms) => agreeTerms, `must agree`),
})
export type MosaicSecondaryApplicantSchema = z.infer<
  typeof secondaryApplicantSchema
>
const VALUES = [
  `AK`,
  `AL`,
  `AR`,
  `AZ`,
  `CA`,
  `CO`,
  `CT`,
  `DC`,
  `DE`,
  `FL`,
  `GA`,
  `HI`,
  `IA`,
  `ID`,
  `IL`,
  `IN`,
  `KS`,
  `KY`,
  `LA`,
  `MA`,
  `MD`,
  `ME`,
  `MI`,
  `MN`,
  `MO`,
  `MS`,
  `MT`,
  `NC`,
  `ND`,
  `NE`,
  `NH`,
  `NJ`,
  `NM`,
  `NV`,
  `NY`,
  `OH`,
  `OK`,
  `OR`,
  `PA`,
  `PR`,
  `RI`,
  `SC`,
  `SD`,
  `TN`,
  `TX`,
  `UT`,
  `VA`,
  `VT`,
  `WA`,
  `WI`,
  `WV`,
  `WY`,
] as const

export const mosaicApplicantSchema = z.object({
  firstName: stringNoNumbersOrSpecialCharacters.refine(
    (v) => v.length > 1,
    `Must be at least 2 characters long`,
  ),
  middleName: stringNoNumbersOrSpecialCharacters.optional(),
  lastName: stringNoNumbersOrSpecialCharacters.refine(
    (v) => v.length > 1,
    `Must be at least 2 characters long`,
  ),
  dob: dateString,
  ssn: last4ssn,
  street: z.string().min(1, `required`),
  city: z.string().min(1, `required`),
  state: z.enum(VALUES),
  zip: z.string().regex(zipCodePattern),
  phoneNumber: z.string().regex(mosaicPhoneNumberFormat),
  email: z.string().email(),
  annualIncome: z.number().min(1),
  otherHouseholdIncome: z.number(),
  ownership: z.boolean(),
  resident: z.boolean(),
  agreeTerms: z.boolean().refine((agreeTerms) => agreeTerms, `must agree`),
  secondary_applicant: z.boolean(),
})
export type MosaicApplicantSchema = z.infer<typeof mosaicApplicantSchema>

export const fullMosaicSchema = mosaicApplicantSchema.merge(
  secondaryApplicantSchema,
)

export type MosaicForm = z.infer<typeof fullMosaicSchema>
